import { Component, type ReactNode } from "react";

/** Did a lazy import fail because its chunk is gone (the app was redeployed)? */
export function isChunkLoadError(error: unknown): boolean {
  const msg = (error as Error | null)?.message ?? "";
  return (
    /dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
      msg,
    ) || (error as Error | null)?.name === "ChunkLoadError"
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Rendered instead of the children once one of them throws. */
  fallback: (error: unknown) => ReactNode;
}

/**
 * Catches a render-time throw below it so one broken piece shows a message
 * instead of unmounting the whole app to a blank page.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, { error: unknown }> {
  state = { error: null as unknown };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  render() {
    return this.state.error ? this.props.fallback(this.state.error) : this.props.children;
  }
}

/** A message plus a Reload button — the fix for a stale build after a deploy. */
export function ReloadNotice({ error, compact }: { error: unknown; compact?: boolean }) {
  const updated = isChunkLoadError(error);
  return (
    <div
      className={`flex flex-wrap items-center gap-3 text-sm text-slate-600 ${
        compact ? "px-3 py-4" : "h-screen justify-center"
      }`}
    >
      <span>
        {updated
          ? "Kandoo was updated since this page loaded."
          : "Something went wrong."}
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded bg-blue-600 px-3 py-1 text-white hover:bg-blue-700"
      >
        Reload
      </button>
    </div>
  );
}
